/**
 * Identifying the Round Off ledger.
 *
 * A bare /round/i test also matches party ledgers that merely contain those
 * letters — "ROUNDO ENTERPRISE" is a real customer, and it sorts before
 * "Roundoff" alphabetically, so an unanchored `.find()` returned the customer
 * and the rounding difference was posted to their account. On the print side
 * the same test pulled their genuine purchase line out of Particulars and into
 * the totals block.
 *
 * So: strip spacing and punctuation, then require the whole name to BE
 * round-off (optionally suffixed with A/c or Account).
 *
 *   "Roundoff"          -> true      "ROUNDO ENTERPRISE"    -> false
 *   "Round Off"         -> true      "Round Office Supplies"-> false
 *   "ROUND-OFF"         -> true      "Groundwork Ltd"       -> false
 *   "Rounding Off A/c"  -> true      "Surround Systems"     -> false
 */
const ROUNDOFF_NAME = /^round(ing)?off(ac|acc|account)?$/;

export const isRoundOffLedgerName = (name?: string | null): boolean =>
  ROUNDOFF_NAME.test(String(name || '').toLowerCase().replace(/[^a-z0-9]/g, ''));

/**
 * Pick the round-off ledger from a list, preferring an exact name match and
 * falling back to a "Round Off ..." prefix (e.g. "Round Off (Purchase)").
 *
 * Deliberately returns undefined rather than falling back to an arbitrary
 * substring hit: no ledger is better than the wrong ledger, because the row is
 * left editable for the user to set.
 */
export const findRoundOffLedger = <T extends { company?: string | null }>(
  ledgers: T[],
): T | undefined =>
  ledgers.find(l => isRoundOffLedgerName(l.company)) ||
  ledgers.find(l => /^round[\s\-_]*off\b/i.test(String(l.company || '').trim()));
